import React, { useEffect, useState } from "react";
import { getCashPrice } from "api/actions/VisitActions";
import BottomFixedButton from "components/bottomFixedButton/BottomFixedButton";
import ChoosePaymentMethod from "components/choosePaymentMethod/ChoosePaymentMethod";
import Header from "components/header/Header";
import MedicalAssistant from "components/MedicalAssistant/MedicalAssistant";
import Spacer from "components/Spacer/Spacer";
import { useApiQuery } from "hooks/useApiQuery";
import { useCurrentUser } from "hooks/useCurrentUser";
import usePatients from "hooks/visit/usePatients";
import { usePrimaryCareVisitFlow } from "hooks/useVisitFlow";
import { primary_care_sub_steps, speciality } from "helpers/enum";
import useModal from "hooks/useModal";
import InsuranceUpdateModal from "../../common/InsuranceUpdateModal";
import { useDispatch } from "react-redux";
import LoaderComponent from "components/loader/LoaderComponent";
import { useHistory } from "react-router-dom";
import useVisitStepChange from "hooks/useVisitStepChange";
import useUpdatePrimaryCareVisit from "hooks/visit/useUpdatePrimaryCareVisit";
import { primaryCareVisitFlowReducers } from "reducers/primary_care/primaryCareVisitFlowReducer";
// Network check removed per new requirement — do not call check-insurance API.
import useToastify from "hooks/useToastify";
import HelpButton from "../../common/HelpButton";
import { trackEvent } from "helpers/analytics";
import { excludeImageContainer } from "helpers/validationSchema";

function PrimaryCareVisitPricing() {
  const { showAlert } = useToastify();
  const [selected, setSelected] = useState(null);
  const [insurance_details, setInsuranceDetails] = useState(null);

  const dispatch = useDispatch();
  const history = useHistory();
  const { setShowModal } = useModal();
  const { userDetails } = useCurrentUser();
  const { loading } = useVisitStepChange();
  const { incompleteData, paymentMethod } = usePrimaryCareVisitFlow();
  const {
    getPatients,
    patientsList,
    fetchingPatients,
    getCurrentPatient,
    getAccountHolder,
  } = usePatients();
  const currentPatient = getCurrentPatient(
    incompleteData?.patient_id,
    patientsList
  );
  const { updateVisit } = useUpdatePrimaryCareVisit();
  const {
    payload: cashPriceResponse,
    loading: fetchingCashPrice,
    query: fetchCashPrice,
  } = useApiQuery(
    getCashPrice(incompleteData?.patient_id, speciality.primary_care),
    false
  );


  function hasInsurance(patient) {
    if (patient?.insurance && patient?.insurance?.insurance_company) {
      return true;
    } else {
      return false;
    }
  }

  useEffect(() => {
    if (paymentMethod) {
      setSelected(paymentMethod);
    }
  }, [paymentMethod, setSelected]);

  useEffect(() => {
    if (patientsList) {
      if (hasInsurance(currentPatient)) {
        setInsuranceDetails(currentPatient?.insurance);
      } else {
        setInsuranceDetails(null);
      }
    }
  }, [
    currentPatient,
    patientsList,
    getAccountHolder,
    getCurrentPatient,
    incompleteData?.patient_id,
  ]);

  useEffect(() => {
    fetchCashPrice();
  }, [fetchCashPrice]);

  useEffect(() => {
    if (insurance_details?.member_id) {
      // Previously we auto-switched to cash when out_of_network was true.
      // New behavior: allow user to proceed with insurance regardless of network.
      setSelected("insurance");
    }
  }, [insurance_details]);

  useEffect(() => {
    getPatients();
  }, [getPatients]);

  async function AcceptPricing() {
    if (selected === "insurance") {
      const keysToCheck = [
        "insurance_company",
        "member_name",
        "relationship_to_patient",
        "member_id",
        "member_dob",
        "insurance_back_cover_photo",
        "insurance_front_cover_photo",
      ];

      for (let key of keysToCheck) {
        if (
          excludeImageContainer.includes(
            insurance_details?.insurance_company
          ) &&
          (key === "insurance_front_cover_photo" ||
            key === "insurance_back_cover_photo")
        ) {
          continue;
        }

        if (
          insurance_details[key] === null ||
          insurance_details[key] === undefined ||
          insurance_details[key] === ""
        ) {
          showAlert("Please provide the missing insurance details", "error");
          return;
        }
      }

      if (insurance_details?.has_secondary_insurance) {
        for (let key of keysToCheck) {
          if (
            excludeImageContainer.includes(
              insurance_details?.secondary_insurance?.insurance_company
            ) &&
            (key === "insurance_front_cover_photo" ||
              key === "insurance_back_cover_photo")
          ) {
            continue;
          }

          if (
            insurance_details?.secondary_insurance[key] === null ||
            insurance_details?.secondary_insurance[key] === undefined ||
            insurance_details?.secondary_insurance[key] === ""
          ) {
            showAlert(
              "Please provide the missing secondary insurance details",
              "error"
            );
            return;
          }
        }
      }
    }

    trackEvent("book_appointment_pc_visit", {
      uid: userDetails?.id,
      paymentMethod: selected,
    });
    updateVisit({
      payload: {
        case: {
          id: incompleteData?.id,
          payment_method: selected === "cash" ? 0 : 1,
          allergies: incompleteData?.allergies_json,
          medications:
            typeof incompleteData?.medications[0] === "string"
              ? incompleteData?.medications.map((item) => ({
                  name: item,
                }))
              : incompleteData?.medications,
          pc_second_phase_steps: {
            ...incompleteData?.pc_second_phase_steps,
            [primary_care_sub_steps.select_patient]: "completed",
          },
        },
      },
      change_current_screen: false,
      screen_to_complete: primary_care_sub_steps.select_patient,
      callBack: () => {
        dispatch(
          primaryCareVisitFlowReducers.setPaymentMethod({
            value: selected,
          })
        );
        showAlert("Progress Saved Successfully", "success");
        setTimeout(() => {
          history.goBack();
        }, 500);
      },
    });
  }

  if (fetchingCashPrice || fetchingPatients) {
    return (
      <LoaderComponent
        showLoader={{
          state: true,
          message: "Please wait",
        }}
      />
    );
  }

  return (
    <div className="h-screen bg-veryLightBlue">
      <Header
        title="Pricing Estimate / Payment Options"
        hideBackBtn={loading}
      />
      <div className="px-8 overflow-y-auto h-eliminateHeaderWithFooter wrapper bg-blue">
        <Spacer height={30} />
        <MedicalAssistant
          content={
            <p className="flex flex-col space-y-4 leading-tight">
              <span>
                {userDetails?.first_name}, let's review pricing options before
                we start.
              </span>
              <span>
                First, we do not charge you until after you've been seen by one
                of our doctors. Feel free to book an appointment at no up-front
                cost.
              </span>
              <span>
                Second, you can trust that we will let you know if you're
                in-network, or out-of-network, before your appointment.
              </span>
              <span>
                Third, we give our patients an estimate of any visit charges.
                Our staff will verify your insurance and benefits once we have
                your health insurance information.
              </span>
            </p>
          }
        />
        <ChoosePaymentMethod
          acceptInsurance={incompleteData?.accept_insurance}
          speciality={speciality.primary_care}
          currentPatient={getCurrentPatient(
            incompleteData?.patient_id,
            patientsList
          )}
          isInsuranceExpired={false}
          cashPriceResponse={cashPriceResponse}
          insuranceDetails={{
            ...insurance_details,
          }}
          onClickAddInsurance={() => {
            setShowModal({
              state: true,
              children: (_, onClose) => (
                <InsuranceUpdateModal
                  onClose={onClose}
                  insuranceDetails={insurance_details}
                  currentPatient={getCurrentPatient(
                    incompleteData?.patient_id,
                    patientsList
                  )}
                  currentSpeciality={speciality.primary_care}
                />
              ),
              data: null,
            });
          }}
          onClickUpdateInsurance={(
            insurance_details,
            currentPatient,
            insuranceType,
            isSecondary
          ) => {
            setShowModal({
              state: true,
              children: (_, onClose) => (
                <InsuranceUpdateModal
                  onClose={onClose}
                  insuranceDetails={insurance_details}
                  currentPatient={currentPatient}
                  currentSpeciality={speciality.primary_care}
                  insuranceType={insuranceType || 1}
                  isSecondary={isSecondary || false}
                />
              ),
              data: null,
            });
          }}
          onSelect={setSelected}
          selected={selected}
        />
      </div>
      <HelpButton bottomClass={"bottom-40"} />
      <BottomFixedButton
        label="Proceed with Booking"
        onClick={!selected ? null : AcceptPricing}
        loading={loading}
        disabled={
          loading ||
          !selected ||
          (selected === "insurance"
            ? insurance_details?.auth_id_required &&
              !incompleteData?.insurance_authorisation?.auth_id
            : false)
        }
      />
    </div>
  );
}

export default PrimaryCareVisitPricing;
